import type { BillingAccessState, BillingSubscriptionStatus } from '../services/payments/types';
import type {
  CustomerBillingAccessState,
  CustomerBillingStatus,
  CustomerRegistrationMode,
} from '../services/registrations/customers/types';
import { formatDate } from './formatDate';
import type { CustomerSubscriptionSnapshot } from './customerSubscription';
import { isBillingEnforcementEnabled } from './planCheckout';

export type { BillingSubscriptionStatus, BillingAccessState };

export interface CustomerBillingContext {
  billingStatus: CustomerBillingStatus | null;
  accessState: CustomerBillingAccessState | null;
  accessBlockedAt: string | null;
  pastDueAt: string | null;
  billingPreference: CustomerSubscriptionSnapshot['billingPreference'];
  chargedPriceCents: number | null;
  commitmentEndsAt: string | null;
  registrationMode: CustomerRegistrationMode | null;
  trialDaysRemaining: number | null;
  subscriptionPlan: { slug: string; name: string };
}

export type BillingUxSeverity = 'info' | 'warning' | 'error';

export interface BillingUxState {
  banner: string | null;
  cta?: string;
  severity?: BillingUxSeverity;
}

export interface BillingStatusPlanSlugSource {
  planSlug: string | null;
}

const GRACE_ACCESS_STATES = new Set<CustomerBillingAccessState>([
  'active',
  'grace_commitment',
  'grace_past_due',
]);

export const mapSnapshotToBillingContext = (
  snapshot: CustomerSubscriptionSnapshot
): CustomerBillingContext => ({
  billingStatus: snapshot.billingStatus,
  accessState: snapshot.accessState,
  accessBlockedAt: snapshot.accessBlockedAt,
  pastDueAt: snapshot.pastDueAt,
  billingPreference: snapshot.billingPreference,
  chargedPriceCents: snapshot.chargedPriceCents,
  commitmentEndsAt: snapshot.commitmentEndsAt,
  registrationMode: snapshot.registrationMode,
  trialDaysRemaining: snapshot.trialDaysRemaining,
  subscriptionPlan: {
    slug: snapshot.planSlug ?? '',
    name: snapshot.planName ?? '',
  },
});

const formatAccessDeadline = (isoDate: string | null): string | null => {
  if (!isoDate) return null;
  const formatted = formatDate(isoDate);
  return formatted || null;
};

/** Assinatura paga ativa (sem graça). */
export const hasPaidAccess = (
  ctx: Pick<CustomerBillingContext, 'accessState' | 'billingStatus'>
): boolean =>
  ctx.accessState === 'active' || (ctx.accessState == null && ctx.billingStatus === 'active');

export const isAccessBlocked = (ctx: Pick<CustomerBillingContext, 'accessState'>): boolean =>
  ctx.accessState === 'blocked';

/** Trial ativo ou acesso permitido pelo backend (inclui graças). */
export const canUseApp = (
  ctx: Pick<CustomerBillingContext, 'accessState' | 'billingStatus' | 'trialDaysRemaining'>
): boolean => {
  if ((ctx.trialDaysRemaining ?? 0) > 0) return true;
  if (ctx.accessState && GRACE_ACCESS_STATES.has(ctx.accessState)) return true;
  if (ctx.accessState == null && ctx.billingStatus === 'active') return true;
  return false;
};

/** Acesso a features pagas — espelha o backend. */
export const canUsePaidFeatures = canUseApp;

export const resolveBillingUx = (
  ctx: Pick<
    CustomerBillingContext,
    'accessState' | 'accessBlockedAt' | 'billingStatus' | 'registrationMode'
  >
): BillingUxState => {
  const deadline = formatAccessDeadline(ctx.accessBlockedAt);

  switch (ctx.accessState) {
    case 'grace_commitment':
      return {
        banner: deadline
          ? `Compromisso anual encerrou — renove até ${deadline}`
          : 'Compromisso anual encerrou — renove para continuar',
        cta: '/assinar',
        severity: 'warning',
      };
    case 'grace_past_due':
      return {
        banner: deadline
          ? `Pagamento em atraso — regularize até ${deadline}`
          : 'Pagamento em atraso — regularize para continuar',
        cta: '/assinar',
        severity: 'warning',
      };
    case 'blocked':
      return {
        banner: 'Acesso bloqueado — renove para continuar',
        cta: '/assinar',
        severity: 'error',
      };
    case 'active':
      return { banner: null };
    default:
      break;
  }

  switch (ctx.billingStatus) {
    case 'pending':
      return {
        banner: 'Aguardando confirmação do pagamento',
        cta: '/assinar',
        severity: 'warning',
      };
    case 'active':
      return { banner: null };
    case 'past_due':
      return {
        banner: 'Pagamento em atraso',
        cta: '/assinar',
        severity: 'warning',
      };
    case 'cancelled':
      return {
        banner: 'Assinatura encerrada — renove para continuar',
        cta: '/assinar',
        severity: 'error',
      };
    case null:
      if (ctx.registrationMode === 'subscribe') {
        return { banner: 'Complete sua assinatura', cta: '/assinar', severity: 'warning' };
      }
      return { banner: null };
    default:
      return { banner: null };
  }
};

/**
 * Slug para renovação/checkout após cancelamento ou atraso.
 * Preferência: billing-status.planSlug (plano contratado) sobre customers/me (cadastro).
 */
export const getRenewalPlanSlug = (
  ctx: Pick<CustomerBillingContext, 'billingStatus' | 'accessState' | 'subscriptionPlan'>,
  billingStatus?: BillingStatusPlanSlugSource | null
): string | null => {
  const billingPlanSlug = billingStatus?.planSlug?.trim() || null;

  const needsRenewalSlug =
    ctx.accessState === 'blocked' ||
    ctx.accessState === 'grace_commitment' ||
    ctx.accessState === 'grace_past_due' ||
    ctx.billingStatus === 'cancelled' ||
    ctx.billingStatus === 'past_due' ||
    ctx.billingStatus === 'pending';

  if (needsRenewalSlug) {
    return billingPlanSlug ?? (ctx.subscriptionPlan.slug.trim() || null);
  }

  return ctx.subscriptionPlan.slug.trim() || billingPlanSlug;
};

export const shouldFetchBillingStatus = (
  snapshot: Pick<CustomerSubscriptionSnapshot, 'billingStatus' | 'accessState'>,
  pathname: string
): boolean => {
  if (!isBillingEnforcementEnabled()) return false;

  if (pathname === '/assinar' || pathname.startsWith('/assinar/')) {
    return true;
  }

  if (
    snapshot.accessState === 'grace_commitment' ||
    snapshot.accessState === 'grace_past_due' ||
    snapshot.accessState === 'blocked'
  ) {
    return true;
  }

  const status = snapshot.billingStatus;
  return status === 'cancelled' || status === 'past_due' || status === 'pending';
};

export const getBillingStatusLabel = (
  ctx: Pick<
    CustomerBillingContext,
    'accessState' | 'accessBlockedAt' | 'billingStatus' | 'trialDaysRemaining'
  >
): string | null => {
  const deadline = formatAccessDeadline(ctx.accessBlockedAt);

  if (ctx.accessState === 'active') return 'Assinatura ativa';
  if (ctx.accessState === 'grace_commitment') {
    return deadline ? `Graça compromisso até ${deadline}` : 'Graça compromisso — renove em breve';
  }
  if (ctx.accessState === 'grace_past_due') {
    return deadline ? `Graça pagamento até ${deadline}` : 'Graça pagamento — regularize em breve';
  }
  if (ctx.accessState === 'blocked') return 'Acesso bloqueado';

  if (ctx.billingStatus === 'active') return 'Assinatura ativa';
  if (ctx.billingStatus === 'cancelled') return 'Assinatura encerrada';
  if (ctx.billingStatus === 'past_due') return 'Pagamento em atraso';
  if (ctx.billingStatus === 'pending') return 'Aguardando pagamento';

  if ((ctx.trialDaysRemaining ?? 0) > 0) {
    const days = ctx.trialDaysRemaining ?? 0;
    const dayLabel = days === 1 ? 'dia' : 'dias';
    return `Período de teste — ${days} ${dayLabel}`;
  }
  return null;
};

export const getAccessStateLabel = (
  accessState: CustomerBillingAccessState | null
): string | null => {
  if (!accessState) return null;
  const labels: Record<CustomerBillingAccessState, string> = {
    active: 'Ativo',
    grace_commitment: 'Graça compromisso',
    grace_past_due: 'Graça pagamento',
    blocked: 'Bloqueado',
  };
  return labels[accessState];
};
